import { inIFrame } from '@thoughtspot/browser-util';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import React, { Children, cloneElement, useEffect, useState } from 'react';
import { Key } from 'w3c-keys';
import { ALLOWED_CSS_PROPERTIES, ALLOWED_HTML_ATTRIBUTES, ALLOWED_HTML_TAGS, DOM_NODE_NAMES, SCROLL_LOCK_ATTR, } from './constants';
export function cloneChildren(children, props) {
    return Children.map(
    // remove null item
    Children.toArray(children).filter((child) => Boolean(child)), (item) => cloneElement(item, props));
}
export function getHashForString(str) {
    let hash = 0;
    if (str) {
        [...str].forEach((_letter, index) => {
            const charCode = str.charCodeAt(index);
            hash += charCode;
        });
    }
    return hash;
}
export function generateUUID() {
    /* eslint-disable no-bitwise, no-mixed-operators */
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
    /* eslint-enable no-bitwise, no-mixed-operators */
}
export function parseDOMString(html, querySelector, text) {
    const domparser = new DOMParser();
    const doc = domparser.parseFromString(html, 'text/html');
    const elem = doc.querySelector(querySelector);
    elem.insertAdjacentText('beforeend', text);
    return doc.body.innerHTML;
}
/**
 * @returns new html by removing
 * element selected by queryselector
 */
export function removeElement(html, querySelector) {
    const domparser = new DOMParser();
    const doc = domparser.parseFromString(html, 'text/html');
    const elem = doc.querySelector(querySelector);
    elem.remove();
    return doc.body.innerHTML;
}
/**
 * @returns function
 * helper method to generate unique
 * key props for react component
 */
const generateUniqueKey = () => {
    let counter = 1;
    const map = new WeakMap();
    const rduid = (item, index) => {
        if (typeof item === 'number' || typeof item === 'string') {
            return index ? `rd-idx-${index}` : `rd-val-${item}`;
        }
        if (!map.has(item)) {
            map.set(item, (counter += 1));
            return rduid(item);
        }
        return `rd-id-${map.get(item)}`;
    };
    return rduid;
};
export const getUniqueKey = generateUniqueKey();
/** React hooks to handle keys */
export const useKeyPressNavigation = (targetElement, items, itemSelectCallBack = _.noop, isVisible) => {
    const [cursor, setCursor] = useState(-1);
    const handleKeyDown = (event) => {
        if (event.key === Key.ArrowUp && isVisible) {
            event.preventDefault();
            if (cursor === -1) {
                setCursor(0);
            }
            else {
                setCursor((prevState) => prevState > 0 ? prevState - 1 : (items === null || items === void 0 ? void 0 : items.length) - 1);
            }
        }
        else if (event.key === Key.ArrowDown && isVisible) {
            event.preventDefault();
            if (cursor === -1) {
                setCursor(0);
            }
            else {
                setCursor((prevState) => prevState < (items === null || items === void 0 ? void 0 : items.length) - 1 ? prevState + 1 : 0);
            }
        }
        else if (event.key === Key.Escape || event.key === Key.Tab) {
            setCursor(-1);
        }
        else if (event.key === Key.Enter) {
            itemSelectCallBack(items[cursor]);
        }
    };
    const handleMouseDown = () => {
        setCursor(-1);
    };
    useEffect(() => {
        if (!isVisible)
            setCursor(-1);
    }, [isVisible]);
    useEffect(() => {
        if (targetElement) {
            targetElement.addEventListener('keydown', handleKeyDown);
            targetElement.addEventListener('mousedown', handleMouseDown);
        }
        return () => {
            if (targetElement) {
                targetElement.removeEventListener('keydown', handleKeyDown);
                targetElement.removeEventListener('mousedown', handleMouseDown);
            }
        };
    });
    return { cursor, setCursor };
};
/**
 * Without this hack, story shots (which we use for snapshot testing)
 * will fail if your story contains a React hook.
 *
 * How to use: Wrap your story which uses a hook inside this function.
 *
 * export const exampleStory = createStoryWithHooks(() => {
 *     // write the story which uses a hook here
 * });
 *
 * https://github.com/storybookjs/storybook/issues/8177
 */
export const createStoryWithHooks = (StoryFn) => () => React.createElement(StoryFn, null);
/**
 * Given an object, return an array of keys that are not
 * extra properties added by react-docgen-typescript-loader
 *
 * @returns array of valid keys that are not added by docgen-loader
 */
export const getNonDocgenKeys = (enumObj) => {
    const blacklistedKeys = ['displayName', '__docgenInfo'];
    return Object.keys(enumObj).filter((key) => !blacklistedKeys.includes(key));
};
/**
 * Filter out extra properties added by react-docgen-typescript-loader
 *
 * @returns string array of enum values
 */
export const getEnumValues = (enumObj) => getNonDocgenKeys(enumObj).map((key) => enumObj[key]);
/**
 * Filter out extra properties added by react-docgen-typescript-loader
 * Useful for using enums as knobs. See icon.stories.tsx
 *
 * @returns enum object without docgen properties
 */
export const getEnumKnobs = (enumObj) => getNonDocgenKeys(enumObj).reduce((obj, key) => ({ ...obj, [key]: enumObj[key] }), {});
export var RD_COMPONENTS;
(function (RD_COMPONENTS) {
    RD_COMPONENTS["TEXTAREA"] = "TEXTAREA";
    RD_COMPONENTS["INPUT_TEXT"] = "INPUT_TEXT";
    RD_COMPONENTS["INPUT_NUMBER"] = "INPUT_NUMBER";
    RD_COMPONENTS["DROPDOWN"] = "DROPDOWN";
    RD_COMPONENTS["INPUT_FILE"] = "INPUT_FILE";
    RD_COMPONENTS["MULTI_SELECT_DROPDOWN"] = "MULTI_SELECT_DROPDOWN";
    RD_COMPONENTS["MULTI_SELECT_CHECKBOX_DROPDOWN"] = "MULTI_SELECT_CHECKBOX_DROPDOWN";
})(RD_COMPONENTS || (RD_COMPONENTS = {}));
export var ScreenSizeClass;
(function (ScreenSizeClass) {
    ScreenSizeClass[ScreenSizeClass["HD"] = 0] = "HD";
    ScreenSizeClass[ScreenSizeClass["FHD"] = 1] = "FHD";
    ScreenSizeClass[ScreenSizeClass["QHD"] = 2] = "QHD";
    ScreenSizeClass[ScreenSizeClass["UHD"] = 3] = "UHD";
})(ScreenSizeClass || (ScreenSizeClass = {}));
const hdScalingClassName = 'rd-scaling-hd';
const fhdScalingClassName = 'rd-scaling-fhd';
const qhdScalingClassName = 'rd-scaling-qhd';
const uhdScalingClassName = 'rd-scaling-uhd';
export const SCALING_CLASS_NAMES = [
    hdScalingClassName,
    fhdScalingClassName,
    qhdScalingClassName,
    uhdScalingClassName,
];
const ResponsiveBreakpoints = [
    {
        name: 'HD',
        screenSizeClass: ScreenSizeClass.HD,
        minWidth: 0,
        maxWidth: 1600,
        className: hdScalingClassName,
    },
    {
        name: 'FHD',
        screenSizeClass: ScreenSizeClass.FHD,
        minWidth: 1600,
        maxWidth: 1920,
        className: fhdScalingClassName,
    },
    {
        name: 'QHD',
        screenSizeClass: ScreenSizeClass.QHD,
        minWidth: 1920,
        maxWidth: 2560,
        className: qhdScalingClassName,
    },
    {
        name: 'UHD',
        screenSizeClass: ScreenSizeClass.UHD,
        minWidth: 2560,
        maxWidth: Infinity,
        className: uhdScalingClassName,
    },
];
/**
 * Checks the screen size class from the current window width
 * @returns Responsive breakpoint object
 */
export const getResponsiveBreakpoint = () => {
    const screenWidth = inIFrame() ? window.innerWidth : window.outerWidth;
    const currentBreakpoint = ResponsiveBreakpoints.find((breakpoint) => {
        return (screenWidth >= breakpoint.minWidth &&
            screenWidth <= breakpoint.maxWidth);
    });
    return currentBreakpoint;
};
/**
 * Pass the onClick handler function as onEnter, onKeyDown is optional
 * Handles the elements being accessible using keyboard
 *
 * @returns the callback to be assigned to the onKeyDown event handler
 */
export const getOnEnter = (onEnter, onKeyDown) => {
    return onKeyDown || onEnter
        ? (event) => {
            if (event.key === Key.Enter && onEnter) {
                onEnter(event);
            }
            if (onKeyDown) {
                onKeyDown(event);
            }
        }
        : _.noop;
};
export const copyTextToClipboard = (text, onCopy) => {
    if (!_.isString(text)) {
        return;
    }
    let textArea = document.querySelector('#copy-text');
    if (!textArea) {
        textArea = document.createElement('textarea');
        textArea.id = 'copy-text';
        textArea.style.position = 'absolute';
        textArea.style.left = '-1000px';
        textArea.style.top = `${document.body.scrollTop}px`;
        document.body.appendChild(textArea);
    }
    textArea.value = text;
    textArea.select();
    textArea.focus();
    document.execCommand('copy');
    setTimeout(() => {
        if (textArea) {
            textArea.value = '';
        }
        if (onCopy) {
            onCopy();
        }
    }, 100);
};
/** Returns true if the browser used is Safari or else false */
export const isSafari = () => {
    // Get the user-agent string for checking the browser type
    const userAgentString = navigator.userAgent;
    // Detect Chrome Browser
    const isChromeBrowser = userAgentString.indexOf('Chrome') > -1;
    // Detect Safari Browser
    let isSafariBrowser = userAgentString.indexOf('Safari') > -1;
    // Discard Safari since it also matches Chrome
    if (isChromeBrowser && isSafariBrowser) {
        isSafariBrowser = false;
    }
    return isSafariBrowser;
};
/**
     * Return the top and left positions of the element relative to document.
     *
     * @param {HTMLDivElement} element - HTML Div element
     * @return {targetTop: number, targetLeft: number}

    */
export const getPositionInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return {
        top: rect.top + scrollTop,
        left: rect.left + scrollLeft,
    };
};
// Enabling re-use of context object for better performance
let canvasContext;
/**
 * Uses canvas.measureText to compute and return the width of the given text of given font
 * in pixels.
 * Prefer this function over getPixelWidthOfString
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with
 * (e.g. "bold 14px verdana").
 *
 * @see http://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export const getPixelWidthOfStringUsingCanvas = (text, font) => {
    canvasContext = document.createElement('canvas').getContext('2d');
    if (canvasContext) {
        canvasContext.font = font;
        const metrics = canvasContext.measureText(text);
        return metrics.width;
    }
    return 0;
};
/**
 *  Take CSS property-value pairs and validate against white-list,
 *  then add the styles to an array of property-value pairs and return it
 */
const validateStyles = (styles) => {
    const output = [];
    if (styles) {
        for (let i = 0; i < styles.length; i++) {
            const styleKey = styles[i];
            const normalizedKey = styleKey
                .replace(/([A-Z])/g, '-$1')
                .toLowerCase();
            if (ALLOWED_CSS_PROPERTIES.includes(normalizedKey)) {
                const value = styles.getPropertyValue(styleKey);
                output.push(`${normalizedKey}: ${value};`);
            }
        }
    }
    return output;
};
// Add hooks to the custom DOMPurify object to remove disallowed CSS Properties
export const addSanitizeAttributesHooks = (customPurify) => {
    // Add a hook to enforce CSS attribute sanitization
    customPurify.addHook('afterSanitizeAttributes', (node) => {
        // Check all style attribute values and validate them
        if (node === null || node === void 0 ? void 0 : node.hasAttribute('style')) {
            const allowedStyles = validateStyles(node === null || node === void 0 ? void 0 : node.style);
            // re-add styles in case any are left
            if (allowedStyles === null || allowedStyles === void 0 ? void 0 : allowedStyles.length) {
                node.setAttribute('style', allowedStyles.join(''));
            }
            else {
                node.removeAttribute('style');
            }
        }
        if ((node === null || node === void 0 ? void 0 : node.nodeName) === DOM_NODE_NAMES.ANCHOR &&
            node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
};
/**
 * This function takes "dirty" HTML content as input and processes it to remove any potentially harmful
 * or unwanted elements, attributes, and styles, based on predefined allowed lists. It helps prevent
 * security vulnerabilities such as Cross-Site Scripting (XSS) by ensuring that only safe and allowed
 * HTML elements, attributes, and CSS properties are retained in the final output.
 * */
export const sanitizeHtml = (dirtyHtml) => {
    const customPurify = DOMPurify();
    // Add hooks to process HTML content to remove disallowed CSS properties
    addSanitizeAttributesHooks(customPurify);
    // Sanitize HTML content using DOMPurify
    const cleanHTML = customPurify.sanitize(dirtyHtml, {
        ALLOWED_TAGS: ALLOWED_HTML_TAGS,
        ALLOWED_ATTR: ALLOWED_HTML_ATTRIBUTES,
    });
    customPurify.removeAllHooks();
    return cleanHTML;
};
export var SchedulerVariants;
(function (SchedulerVariants) {
    SchedulerVariants["V1"] = "v1";
    SchedulerVariants["V2"] = "v2";
})(SchedulerVariants || (SchedulerVariants = {}));
/**
 * manipulate scroll actions on <body /> by adding a screen behind the context menu`
 */
export function getDOMScrollLockActions() {
    const APP_ROOT_ID = 'root';
    const noActions = {
        disableScroll: _.noop,
        enableScroll: _.noop,
    };
    const preventDefault = (e) => e.preventDefault();
    try {
        const rootEle = document.getElementById(APP_ROOT_ID);
        if (!rootEle) {
            throw new Error('no element');
        }
        const actions = { ...noActions };
        actions.disableScroll = () => {
            rootEle.addEventListener('wheel', preventDefault);
            rootEle.addEventListener('touchmove', preventDefault, {
                passive: false,
            });
            rootEle.setAttribute(SCROLL_LOCK_ATTR, '1');
        };
        actions.enableScroll = () => {
            rootEle.removeEventListener('wheel', preventDefault);
            rootEle.removeEventListener('touchmove', preventDefault);
            rootEle.removeAttribute(SCROLL_LOCK_ATTR);
        };
        return actions;
    }
    catch (_a) {
        return noActions;
    }
}
export * from './testing';
//# sourceMappingURL=index.js.map